import React, { useState } from "react";
import { Box, Button, VStack, Text } from "@chakra-ui/react";
import ReactMarkdown from "react-markdown";
import { formatAIMessage } from "../utils/messageFormatting";

const FormatTestComponent = () => {
  const [showTest, setShowTest] = useState(false);

  // Test message that simulates the problematic formatting from your screenshot
  const testMessage = `Suggested Content: To enhance the **Insight Sprint** and effectively address the design challenge of optimizing the checkout process for a multi-vendor e-commerce platform, I recommend the following improvements:

### 1. **Include User Interviews and Usability Testing Early**
   - **User Interviews** (8h) are already part of the research tools. Consider increasing the duration or frequency to gather deeper insights about user experiences and pain points specifically related to the checkout process.
   - **Usability Testing** should be emphasized during the **Synthesis** stage to validate findings from earlier user research activities.

### 2. **Expand Survey Scope**
   - The existing **Surveys** (4h) might be limited in their reach. Consider creating targeted surveys that focus specifically on checkout experiences, frustrations, and desired features to gather quantitative data.

### 3. **Increase Focus on Feedback Integration**
   - Strengthen the **Feedback** stage by incorporating an additional tool for iterative feedback loops. This could involve setting up **Follow-up Interviews** or **Feedback Workshops** to ensure user insights are continuously integrated into the design process.

### 4. **Data-Driven Analysis**
   - Ensure that the **Analytics Analysis** (1h) is robust by possibly adding tools for deeper behavioral analysis or metrics specifically related to checkout abandonment. This could help in understanding user behavior trends more effectively.

### 5. **Incorporate Competitive Analysis**
   - Add a tool for **Competitive Analysis** (e.g., analyzing competitors' checkout processes) to identify best practices and innovative solutions that could be applied to your platform.

### Revised Tool Allocation
You might consider modifying the **Insight Sprint** to include additional hours or tools as follows:

- **Add User Interviews** duration (e.g., increase to 12h)
- **Expand Surveys** duration (e.g., increase to 8h)
- **Include Follow-up Feedback Workshops** (e.g., 4h)
- **Add Competitive Analysis** (e.g., 3h)

### Next Steps
If you decide to implement these improvements, I can assist you in modifying the **Insight Sprint** program by updating the relevant stages and tools. Would you like to proceed with this adjustment?`;

  const formattedMessage = formatAIMessage(testMessage);

  return (
    <Box p={6} maxW="800px" mx="auto">
      <VStack spacing={4}>
        <Button onClick={() => setShowTest(!showTest)} colorScheme="blue">
          {showTest ? "Hide" : "Show"} Formatting Test
        </Button>

        {showTest && (
          <VStack spacing={6} align="stretch">
            <Box>
              <Text fontWeight="bold" mb={2}>
                Raw Message:
              </Text>
              <Box bg="gray.100" p={4} borderRadius="md" fontSize="sm">
                <Text whiteSpace="pre-wrap">{testMessage}</Text>
              </Box>
            </Box>

            <Box>
              <Text fontWeight="bold" mb={2}>
                Formatted Message:
              </Text>
              <Box bg="gray.100" p={4} borderRadius="md" fontSize="sm">
                <Text whiteSpace="pre-wrap">{formattedMessage}</Text>
              </Box>
            </Box>

            <Box>
              <Text fontWeight="bold" mb={2}>
                Rendered with ReactMarkdown:
              </Text>
              <Box
                bg="white"
                p={4}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
              >
                <ReactMarkdown
                  children={formattedMessage}
                  components={{
                    p: ({ children }) => (
                      <Text
                        mb={3}
                        lineHeight="1.7"
                        color="gray.700"
                        whiteSpace="pre-wrap"
                      >
                        {children}
                      </Text>
                    ),

                    strong: ({ children }) => (
                      <Text as="strong" fontWeight="bold" color="gray.800">
                        {children}
                      </Text>
                    ),

                    ol: ({ children }) => (
                      <Box
                        as="ol"
                        pl={6}
                        mb={4}
                        mt={2}
                        sx={{
                          listStyleType: "decimal",
                          listStylePosition: "outside",
                          "& li": {
                            marginBottom: "8px",
                            lineHeight: "1.6",
                          },
                        }}
                      >
                        {children}
                      </Box>
                    ),

                    ul: ({ children }) => (
                      <Box
                        as="ul"
                        pl={6}
                        mb={4}
                        mt={2}
                        sx={{
                          listStyleType: "disc",
                          listStylePosition: "outside",
                          "& li": {
                            marginBottom: "8px",
                            lineHeight: "1.6",
                          },
                        }}
                      >
                        {children}
                      </Box>
                    ),

                    li: ({ children }) => (
                      <Box as="li" mb={2} lineHeight="1.6" color="gray.700">
                        {children}
                      </Box>
                    ),

                    h1: ({ children }) => (
                      <Text
                        fontSize="xl"
                        fontWeight="bold"
                        mb={3}
                        mt={4}
                        color="gray.800"
                      >
                        {children}
                      </Text>
                    ),

                    h2: ({ children }) => (
                      <Text
                        fontSize="lg"
                        fontWeight="bold"
                        mb={3}
                        mt={4}
                        color="gray.800"
                      >
                        {children}
                      </Text>
                    ),

                    h3: ({ children }) => (
                      <Text
                        fontSize="md"
                        fontWeight="bold"
                        mb={2}
                        mt={3}
                        color="gray.800"
                      >
                        {children}
                      </Text>
                    ),

                    br: () => <Box mb={2} />,

                    // Handle code blocks
                    code: ({ inline, children }) => {
                      if (inline) {
                        return (
                          <Text
                            as="code"
                            bg="gray.100"
                            px={1}
                            py={0.5}
                            borderRadius="sm"
                            fontSize="sm"
                          >
                            {children}
                          </Text>
                        );
                      }
                      return (
                        <Box
                          as="pre"
                          bg="gray.100"
                          p={3}
                          borderRadius="md"
                          overflowX="auto"
                          mb={3}
                        >
                          <Text as="code" fontSize="sm">
                            {children}
                          </Text>
                        </Box>
                      );
                    },

                    // Handle text nodes with proper line breaks
                    text: ({ children }) => {
                      if (typeof children === "string") {
                        return children
                          .split("\n")
                          .map((line, index, array) => (
                            <React.Fragment key={index}>
                              {line}
                              {index < array.length - 1 && <br />}
                            </React.Fragment>
                          ));
                      }
                      return children;
                    },
                  }}
                />
              </Box>
            </Box>
          </VStack>
        )}
      </VStack>
    </Box>
  );
};

export default FormatTestComponent;
